import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  CONSENT_STORAGE_KEY,
  ConsentValue,
  getStoredConsent,
  initClarity,
  setStoredConsent,
} from "@/lib/clarity";

/**
 * Public cookie policy page (/cookie-policy).
 *
 * Covers Microsoft Clarity (active) and basic Google Analytics (mentioned
 * as standard tracking that may be enabled in the future on the same
 * consent). No PII, no advertising profiles, no data sold or shared with
 * advertisers. Visitors can update their choice from this page at any
 * time.
 */
export default function CookiePolicy() {
  const [consent, setConsent] = useState<ConsentValue | null>(null);

  useEffect(() => {
    setConsent(getStoredConsent());
  }, []);

  const apply = (v: ConsentValue) => {
    setStoredConsent(v);
    setConsent(v);
    if (v === "granted") initClarity();
    // Notify other tabs / the banner.
    try {
      window.dispatchEvent(new StorageEvent("storage", { key: CONSENT_STORAGE_KEY }));
    } catch {
      /* ignore */
    }
  };

  return (
    <article className="prose prose-slate max-w-3xl mx-auto py-2">
      <h1 className="text-2xl md:text-3xl font-semibold text-slate-900">Cookie Policy</h1>
      <p className="text-sm text-slate-500 mt-1">Last updated: May 28, 2026</p>

      <p className="mt-6 text-slate-700 leading-relaxed">
        DigaVouch uses a small number of cookies and similar storage technologies
        to keep the product working and to understand how it is used so we can
        improve it. This page explains what we use and why, and lets you change
        your choice at any time.
      </p>

      <h2 className="mt-8 text-lg font-semibold text-slate-900">What we don't do</h2>
      <ul className="mt-2 list-disc pl-5 text-slate-700 text-sm leading-relaxed space-y-1">
        <li>We do <strong>not</strong> collect personally identifiable information (PII) for analytics.</li>
        <li>We do <strong>not</strong> use cookies for advertising, retargeting, or building advertising profiles.</li>
        <li>We do <strong>not</strong> sell or share analytics data with advertisers or data brokers.</li>
        <li>We do <strong>not</strong> track you across unrelated third-party websites.</li>
      </ul>

      <h2 className="mt-8 text-lg font-semibold text-slate-900">Categories of cookies we use</h2>

      <h3 className="mt-4 text-base font-semibold text-slate-900">1. Strictly necessary</h3>
      <p className="text-slate-700 text-sm leading-relaxed">
        Required for the product to work — authentication, session continuity,
        security (CSRF protection), and remembering UI preferences such as the
        sidebar open/closed state. These cannot be turned off.
      </p>

      <h3 className="mt-4 text-base font-semibold text-slate-900">2. Product analytics (optional)</h3>
      <p className="text-slate-700 text-sm leading-relaxed">
        Used only after you accept. We use these to understand which screens
        are used, where users get stuck, and how to improve the flow. We do not
        collect PII, and we never sell or share this data with advertisers.
      </p>
      <ul className="mt-2 list-disc pl-5 text-slate-700 text-sm leading-relaxed space-y-1">
        <li>
          <strong>Microsoft Clarity</strong> — anonymized session replays and
          heatmaps. We rely on Clarity's built-in masking, which obscures input
          fields and text content by default. Data is processed by Microsoft;
          see{" "}
          <a
            href="https://privacy.microsoft.com/en-us/privacystatement"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Microsoft's privacy statement
          </a>
          .
        </li>
        <li>
          <strong>Google Analytics (basic)</strong> — if enabled, used for
          aggregate page-view and traffic-source counts only. IP anonymization
          is enabled, advertising signals are disabled, and we do not link
          analytics to any advertising product. See{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Google's privacy policy
          </a>
          .
        </li>
      </ul>

      <h2 className="mt-8 text-lg font-semibold text-slate-900">How long we keep data</h2>
      <p className="text-slate-700 text-sm leading-relaxed">
        Analytics data is retained according to each provider's default
        retention (Microsoft Clarity: ~13 months; Google Analytics: configured
        to 14 months). Your consent choice is stored in your browser's local
        storage until you change it or clear your browser data.
      </p>

      <h2 className="mt-8 text-lg font-semibold text-slate-900">Your choice</h2>
      <p className="text-slate-700 text-sm leading-relaxed">
        Current status:{" "}
        <strong>
          {consent === "granted"
            ? "Analytics accepted"
            : consent === "denied"
              ? "Analytics declined"
              : "No choice recorded yet"}
        </strong>
        .
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => apply("granted")} className="bg-slate-900 text-white hover:bg-slate-800">
          Accept analytics
        </Button>
        <Button size="sm" variant="outline" onClick={() => apply("denied")}>
          Decline analytics
        </Button>
      </div>

      <h2 className="mt-8 text-lg font-semibold text-slate-900">Contact</h2>
      <p className="text-slate-700 text-sm leading-relaxed">
        Questions about this policy? Email{" "}
        <a href="mailto:privacy@digavouch.com" className="underline">privacy@digavouch.com</a>{" "}
        or see our{" "}
        <a href="https://digavouch.com/privacy" className="underline">privacy policy</a>.
      </p>

      <p className="mt-8 text-xs text-slate-500">
        <Link to="/" className="underline">Return home</Link>
      </p>
    </article>
  );
}
